import Swiper from "swiper";
import { SwiperOptions } from "swiper/types";
// swiper/css is already imported in main.js — no need to duplicate here
// @ts-ignore
import Player from "@vimeo/player";

const isIOS = () =>
	/iP(hone|od|ad)/.test(navigator.platform) || (navigator.userAgent.includes("Mac") && "ontouchend" in window);

export class VerticalVideoCarousel {
	private element!: HTMLElement;
	private swiper!: Swiper;
	private aspectRatio: string;
	private swiperContainer!: HTMLElement;
	private vimeoPlayers: Map<HTMLElement, any> = new Map();
	private currentActivePlayer: any | null = null;
	private breakpoints: any;

	constructor(element: HTMLElement) {
		this.element = element;
		this.swiperContainer = this.element.querySelector(".swiper") as HTMLElement;
		this.aspectRatio = this.swiperContainer.getAttribute("data-ratio") as string;

		/*
		 * Optional per-instance override for the desktop count, set by the block's
		 * "Slides per view (desktop)" field. Absent or unparseable falls back to
		 * the 5.4 peek this block has always used, so instances that do not set it
		 * are unchanged. Only the >=1024 breakpoint is overridable: the comp that
		 * motivated this (Careers, Figma 1713:4910) is a desktop frame and says
		 * nothing about tablet, where the 3.4 peek stays.
		 */
		const desktopAttr = this.swiperContainer.getAttribute("data-slides-desktop");
		const desktopParsed = desktopAttr ? parseFloat(desktopAttr) : NaN;

		/*
		 * Desktop default is per-variant, because only the vertical variant has a
		 * comp. Figma 2366:14655 ("Videos", frame 1274:2) draws FIVE WHOLE cards
		 * with a 30px gutter, and that geometry only closes at five:
		 *   5 x 224 + 4 x 30 = 1240, which is the row width at the comp's 1440.
		 * The 5.4 peek this block shipped with cuts a card at each edge, which is
		 * what its original horizontal consumers want and what the comp does not.
		 *
		 * The per-instance field still wins, so the escape hatch added for the
		 * Careers comp is intact — it is simply no longer needed to reach the
		 * comp, since the comp is now the variant's default.
		 */
		const isVertical = this.aspectRatio !== "horizontal";
		const desktopFallback = isVertical ? 5 : 5.4;
		const desktopSlides =
			Number.isFinite(desktopParsed) && desktopParsed > 0 ? desktopParsed : desktopFallback;

		/*
		 * Figma 2366:14655 — 30px between thumbnails. This is Swiper's option, not
		 * CSS: Swiper writes an inline width per slide from slidesPerView and
		 * spaceBetween, so it is the only lever that agrees with its own pitch,
		 * snap grid and translate arithmetic. A `padding-inline` on .swiper-slide
		 * was tried and never took effect.
		 *
		 * 30 is the >=1024 value, off the comp's own desktop board.
		 *
		 * MOBILE IS 20, from the base options below. Client, 2026-08-31: "the video
		 * testimonial slider needs 20px gap between videos for mobile, right now
		 * screen sizes below 767 are showing no gap".
		 *
		 * TABLET IS 20 TOO. QA, 2026-09-02: the 3.4-card peek was still butted at
		 * 768–1023 — the state it had been deliberately left in while no board
		 * covered it, now reviewed and answered with the phone value rather than
		 * the desktop one, because the tablet pitch is the phone's peek layout at
		 * a wider card count, not the desktop grid. Swiper breakpoints INHERIT the
		 * base params, so 20 would carry up on its own; the 768 entries restate it
		 * anyway, so the value that applies in the band is readable where the band
		 * is declared instead of two objects away.
		 */
		if (this.aspectRatio === "horizontal") {
			this.breakpoints = {
				768: { slidesPerView: 3.4, spaceBetween: 20 },
			};
		} else {
			this.breakpoints = {
				768: { slidesPerView: 3.4, spaceBetween: 20 },
				1024: { slidesPerView: desktopSlides, spaceBetween: 30 },
			};
		}

		if (!this.swiperContainer) return;

		const swiperOptions: SwiperOptions = {
			centeredSlides: true,
			slidesPerView: 1.4,
			// Phone only — every breakpoint above restates its own value.
			spaceBetween: 20,
			loop: true,
			initialSlide: 0,
			breakpoints: this.breakpoints,
			on: {
				init: (swiper) => {
					const firstActive = swiper.slides[swiper.activeIndex] as HTMLElement | undefined;
					if (firstActive) this.playVideoInSlide(firstActive, false);
				},

				// CLICK path: already a user gesture — play with sound first, then slide
				click: (swiper, event) => {
					const target = event.target as HTMLElement;
					if (!target) return;

					const clickedSlide = target.closest(".swiper-slide") as HTMLElement;
					if (!clickedSlide) return;

					if (swiper.clickedIndex !== -1) {
						const clickedIsActive = !!swiper.clickedSlide?.classList.contains("swiper-slide-active");

						if (!clickedIsActive) {
							// Play WITH SOUND inside the same gesture for the clicked slide
							this.playVideoInSlide(clickedSlide, true, /*forceWithSound*/ true);
							// Then move to it
							swiper.slideTo(swiper.clickedIndex);
						} else {
							// Active slide clicked: play with sound
							this.playVideoInSlide(clickedSlide, true, /*forceWithSound*/ true);
						}
					}
				},

				// Pause when slide is about to change
				beforeSlideChangeStart: () => {
					if (this.currentActivePlayer) {
						try {
							this.currentActivePlayer.pause();
						} catch {}
					}
				},

				// SWIPE path: run inside the same gesture at touchEnd.
				// Swiper hasn't updated activeIndex yet, so compute the target from swipeDirection.
				touchEnd: (swiper) => {
					const target = this.getSwipeTargetSlide(swiper);
					if (target) {
						if (this.currentActivePlayer) {
							try {
								this.currentActivePlayer.pause();
							} catch {}
						}
						// Play WITH SOUND as part of the swipe gesture
						this.playVideoInSlide(target, true, /*forceWithSound*/ true);
					}
				},

				// Fallback once transition settles (safe to leave as-is)
				slideChangeTransitionEnd: (swiper) => {
					const activeSlide = swiper.slides[swiper.activeIndex] as HTMLElement;
					if (activeSlide) {
						// On iOS this likely already plays due to touchEnd; calling again is harmless
						this.playVideoInSlide(activeSlide, true, /*forceWithSound*/ isIOS());
					}
				},
			},
		};

		this.swiper = new Swiper(this.swiperContainer, swiperOptions);
	}

	// Determine which slide will become active based on the swipe direction.
	// We do this at touchEnd to stay inside the user gesture on iOS.
	private getSwipeTargetSlide(swiper: Swiper): HTMLElement | null {
		const dir = (swiper as any).swipeDirection as "next" | "prev" | undefined;
		const total = swiper.slides.length;
		if (!total) return null;

		// If direction is unknown (short swipe that doesn't trigger slide change),
		// just target the current active slide.
		if (!dir) {
			return swiper.slides[swiper.activeIndex] as HTMLElement;
		}

		let idx = swiper.activeIndex + (dir === "next" ? 1 : -1);
		if (idx < 0) idx = total - 1;
		if (idx >= total) idx = 0;

		return swiper.slides[idx] as HTMLElement;
	}

	private pauseNonActive(swiper: Swiper) {
		const activeEl = swiper.slides[swiper.activeIndex] as HTMLElement;
		this.vimeoPlayers.forEach((p, el) => {
			const activeItem = activeEl.querySelector(".vertical-video-item") as HTMLElement | null;
			if (el !== activeItem && el !== activeEl) {
				try {
					p.pause();
				} catch {}
			}
		});
	}

	private async playActiveWithSound(swiper: Swiper) {
		const activeSlide = swiper.slides[swiper.activeIndex] as HTMLElement;
		if (!activeSlide) return;
		this.playVideoInSlide(activeSlide, true, /*forceWithSound*/ true);
	}

	// Play Vimeo video in the given slide
	private playVideoInSlide(slide: HTMLElement, playVideo: boolean = true, forceWithSound: boolean = false) {
		const outer = slide.querySelector(".vertical-video-item") as HTMLElement | null;
		if (!outer) return;

		const playerHost = outer.querySelector(".vertical-video-item-player") as HTMLElement | null;
		const thumb = outer.querySelector(".vertical-video-item-thumb") as HTMLElement | null;
		const videoUrl = outer.getAttribute("data-url");
		if (!videoUrl) return;

		const setThumbVisible = (visible: boolean) => {
			if (!thumb) return;
			thumb.style.transition = thumb.style.transition || "opacity 220ms ease";
			thumb.style.opacity = visible ? "1" : "0";
			thumb.style.pointerEvents = visible ? "auto" : "none";
		};

		let player = this.vimeoPlayers.get(outer);

		if (!player) {
			try {
				player = new Player(playerHost ?? outer, { url: videoUrl as any });
				this.vimeoPlayers.set(outer, player);

				player.setVolume?.(1).catch(() => {});
				player.setMuted?.(false).catch(() => {});

				player.on?.("play", () => setThumbVisible(false));
				player.on?.("pause", () => setThumbVisible(true));
				player.on?.("ended", () => setThumbVisible(true));
				player.on?.("error", (error: any) => {
					const msg = String(error?.message || "");
					if (error?.name === "PlayInterrupted" || /interrupted/i.test(msg)) return;
					// console.error("Vimeo error:", error);
				});
			} catch {
				return;
			}
		}

		if (this.currentActivePlayer && this.currentActivePlayer !== player) {
			try {
				this.currentActivePlayer.pause();
			} catch {}
		}
		this.currentActivePlayer = player;

		if (!playVideo) return;

		// iOS path: play WITH SOUND inside gesture; no muted fallback
		if (forceWithSound || isIOS()) {
			setThumbVisible(false);
			Promise.resolve()
				.then(() => player.setMuted?.(false))
				.then(() => player.setVolume?.(1))
				.then(() => player.play())
				.catch(() => {
					// If it fails here, it wasn’t truly in-gesture; leave thumb visible again.
					setThumbVisible(true);
				});
			return;
		}

		// Non-iOS fallback with muted autoplay if needed
		setThumbVisible(false);
		player.play().catch((_error: any) => {
			if (typeof player.setMuted === "function") {
				player
					.setMuted(true)
					.then(() => player.play())
					.catch(() => setThumbVisible(true));
			} else if (typeof player.setVolume === "function") {
				player
					.setVolume(0)
					.then(() => player.play())
					.catch(() => setThumbVisible(true));
			} else {
				setThumbVisible(true);
			}
		});
	}
}

document.addEventListener("DOMContentLoaded", () => {
	const blocks = document.querySelectorAll(".block-vertical-video-carousel");
	if (blocks.length === 0) return;
	blocks.forEach((block) => {
		new VerticalVideoCarousel(block as HTMLElement);
	});
});
